import React, { useState } from 'react';
import { Modal, Button, List, Typography, Space, message } from 'antd';
import { FileTextOutlined, DownloadOutlined, EyeOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import { TemplateService, mediationTemplates, litigationTemplates, lawyerTemplates, mediationGuide, litigationGuide, lawyerGuide } from '../../../services/templateService';
import { TemplatePreviewModal } from '../../../components/TemplatePreviewModal';

const { Title, Text } = Typography;

const ModalContent = styled.div`
  padding: 20px;
`;

const DocumentItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  border: 1px solid #e8e8e8;
  border-radius: 6px;
  margin-bottom: 8px;
  background: #fafafa;
`;

const DocumentInfo = styled.div`
  flex: 1;
`;

const DocumentActions = styled.div`
  display: flex;
  gap: 8px;
`;

interface DocumentSupportModalProps {
  visible: boolean;
  onClose: () => void;
  selectedStrategy?: string;
}

interface PreviewModalState {
  visible: boolean;
  templateId?: string;
  templateName?: string;
}

export const DocumentSupportModal: React.FC<DocumentSupportModalProps> = ({
  visible,
  onClose,
  selectedStrategy
}) => {
  const [previewModal, setPreviewModal] = useState<PreviewModalState>({
    visible: false,
    templateId: undefined,
    templateName: undefined
  });
  // 策略名称映射
  const getStrategyName = (strategyId: string) => {
    const strategyNames = {
      'amicable_resolution': '直接交渉で合意を目指す',
      'mediation_conciliation': '調停・あっせん',
      'self_legal_proceedings': '自己で法的手続きを行う',
      'lawyer_legal_proceedings': '弁護士に依頼する'
    };
    return strategyNames[strategyId as keyof typeof strategyNames] || strategyId;
  };

  // 根据策略返回对应的文档列表
  const getDocumentsForStrategy = (strategy: string) => {
    // 如果是"直接交渉で合意を目指す"，返回空数组
    if (strategy === 'amicable_resolution') {
      return [];
    }
    
    // 如果是調停・あっせん，使用調停模板服务
    if (strategy === 'mediation_conciliation') {
      return TemplateService.getAllTemplates('mediation');
    }
    
    // 如果是自己で法的手続きを行う，使用自己訴訟模板服务
    if (strategy === 'self_legal_proceedings') {
      return TemplateService.getAllTemplates('litigation');
    }
    
    // 如果是弁護士に依頼する，使用弁護士依頼模板服务
    if (strategy === 'lawyer_legal_proceedings') {
      return TemplateService.getAllTemplates('lawyer');
    }
    
    return [];
  };

  const documents = getDocumentsForStrategy(selectedStrategy || '');

  const handlePreview = (templateId: string, templateName: string) => {
    // console.log('プレビューボタンがクリックされました:', { templateId, templateName });
    setPreviewModal({
      visible: true,
      templateId,
      templateName
    });
    // console.log('previewModal state updated:', { visible: true, templateId, templateName });
  };

  const handleDownload = async (templateId: string, templateName: string) => {
    try {
      await TemplateService.downloadTemplate(templateId, 'md');
      message.success(`${templateName}をダウンロードしました`);
    } catch (error) {
      console.error('ダウンロードエラー:', error);
      message.error('ダウンロードに失敗しました');
    }
  };

  const closePreviewModal = () => {
    setPreviewModal({
      visible: false,
      templateId: undefined,
      templateName: undefined
    });
  };

  // 如果是"直接交渉で合意を目指す"，显示特殊消息
  if (selectedStrategy === 'amicable_resolution') {
    return (
      <>
        <Modal
          title="📋 書類作成サポート"
          open={visible}
          onCancel={onClose}
          footer={null}
          width={600}
        >
          <ModalContent>
            <div style={{ 
              textAlign: 'center', 
              padding: '40px 20px',
              color: '#666'
            }}>
              <div style={{ 
                fontSize: '16px', 
                marginBottom: '12px',
                color: '#1890ff'
              }}>
                🤝 直接交渉で合意を目指す
              </div>
              <div style={{ 
                fontSize: '14px', 
                lineHeight: '1.6'
              }}>
                この戦略では、正式な書類作成は必要ありません。<br />
                直接的なコミュニケーションと交渉を通じて、<br />
                双方が合意できる解決策を見つけることを目指します。
              </div>
            </div>
          </ModalContent>
        </Modal>

        {/* テンプレートプレビューモーダル */}
        <TemplatePreviewModal
          visible={previewModal.visible}
          onClose={closePreviewModal}
          templateId={previewModal.templateId}
          templateName={previewModal.templateName}
        />
      </>
    );
  }

  // 如果是调解策略，显示特殊内容
  if (selectedStrategy === 'mediation_conciliation') {
    return (
      <>
        <Modal
          title="📋 調停・あっせん手続きサポート"
          open={visible}
          onCancel={onClose}
          footer={null}
          width={800}
        >
          <ModalContent>
            <div style={{ marginBottom: 20 }}>
              <Text>
                日本法院の調停手続きに基づいて、必要な書類と手続きをご案内します。
              </Text>
            </div>

            {/* 调停机构选择指南 */}
            <div style={{ marginBottom: 24, padding: 16, background: '#f0f8ff', borderRadius: 6 }}>
              <Title level={5}>🏛️ 調停機関の選択ガイド</Title>
              <div style={{ fontSize: '13px', lineHeight: '1.6' }}>
                {mediationGuide.institutions.map((institution, index) => (
                  <div key={index} style={{ marginBottom: '8px' }}>
                    <strong>{institution.name}：</strong>{institution.description}
                    <div style={{ marginLeft: '16px', fontSize: '12px', color: '#666' }}>
                      例：{institution.examples.join('、')}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* 申请所需文件 */}
            <div style={{ marginBottom: 24 }}>
              <Title level={5}>📝 申立に必要な書類</Title>
              <List size="small">
                {mediationGuide.requiredDocuments.map((doc, index) => (
                  <List.Item key={index}>• {doc}</List.Item>
                ))}
              </List>
            </div>

            <Title level={4}>📄 利用可能なテンプレート</Title>
            
            {/* 按类别分组显示文档 */}
            {['申立書類', '準備書類', '手続書類', '合意書類'].map(category => {
              const categoryDocs = documents.filter(doc => doc.category === category);
              if (categoryDocs.length === 0) return null;
              
              return (
                <div key={category} style={{ marginBottom: 16 }}>
                  <div style={{ 
                    fontSize: '14px', 
                    fontWeight: '500', 
                    color: '#1890ff',
                    marginBottom: '8px',
                    borderBottom: '1px solid #e8e8e8',
                    paddingBottom: '4px'
                  }}>
                    {category}
                  </div>
                  {categoryDocs.map((doc, index) => (
                    <DocumentItem key={index}>
                      <DocumentInfo>
                        <div style={{ fontWeight: 500, marginBottom: 4 }}>
                          {doc.name}
                        </div>
                        <div style={{ fontSize: '12px', color: '#666' }}>
                          {doc.description}
                        </div>
                      </DocumentInfo>
                      <DocumentActions>
                        <Button 
                          size="small" 
                          icon={<EyeOutlined />}
                          onClick={() => handlePreview((doc as any).id || doc.name, doc.name)}
                        >
                          プレビュー
                        </Button>
                        <Button 
                          size="small" 
                          type="primary"
                          icon={<DownloadOutlined />}
                          onClick={() => handleDownload((doc as any).id || doc.name, doc.name)}
                        >
                          ダウンロード
                        </Button>
                      </DocumentActions>
                    </DocumentItem>
                  ))}
                </div>
              );
            })}

            {/* 程序流程 */}
            <div style={{ marginTop: 20, padding: 16, background: '#f8f9fa', borderRadius: 6 }}>
              <Title level={5}>📋 調停手続きの流れ</Title>
              <List size="small">
                {mediationGuide.procedureSteps.map((step, index) => (
                  <List.Item key={index}>{index + 1}. {step}</List.Item>
                ))}
              </List>
            </div>

            {/* 实用链接 */}
            <div style={{ marginTop: 16, padding: 12, background: '#fff7e6', borderRadius: 6 }}>
              <Title level={5}>🔗 参考リンク</Title>
              <div style={{ fontSize: '12px', lineHeight: '1.6' }}>
                {mediationGuide.referenceLinks.map((link, index) => (
                  <div key={index}>
                    • <a href={link.url} target="_blank" rel="noopener noreferrer">{link.name}</a>
                  </div>
                ))}
              </div>
            </div>
          </ModalContent>
        </Modal>

        {/* テンプレートプレビューモーダル */}
        <TemplatePreviewModal
          visible={previewModal.visible}
          onClose={closePreviewModal}
          templateId={previewModal.templateId}
          templateName={previewModal.templateName}
        />
      </>
    );
  }

  // 如果是自己訴訟策略，显示特殊内容
  if (selectedStrategy === 'self_legal_proceedings') {
    return (
      <>
        <Modal
          title="⚖️ 自己訴訟手続きサポート"
          open={visible}
          onCancel={onClose}
          footer={null}
          width={800}
        >
          <ModalContent>
            <div style={{ marginBottom: 20 }}>
              <Text>
                日本法院の民事訴訟手続きに基づいて、必要な書類と手続きをご案内します。
              </Text>
            </div>

            {/* 法院类型选择指南 */}
            <div style={{ marginBottom: 24, padding: 16, background: '#fff2e8', borderRadius: 6 }}>
              <Title level={5}>🏛️ 裁判所の選択ガイド</Title>
              <div style={{ fontSize: '13px', lineHeight: '1.6' }}>
                {litigationGuide.courtTypes.map((court, index) => (
                  <div key={index} style={{ marginBottom: '8px' }}>
                    <strong>{court.name}：</strong>{court.description}
                    <div style={{ marginLeft: '16px', fontSize: '12px', color: '#666' }}>
                      例：{court.examples.join('、')}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* 申请所需文件 */}
            <div style={{ marginBottom: 24 }}>
              <Title level={5}>📝 訴訟に必要な書類</Title>
              <List size="small">
                {litigationGuide.requiredDocuments.map((doc, index) => (
                  <List.Item key={index}>• {doc}</List.Item>
                ))}
              </List>
            </div>

            {/* 手数料表 */}
            <div style={{ marginBottom: 24, padding: 16, background: '#f6ffed', borderRadius: 6 }}>
              <Title level={5}>💰 手数料一覧</Title>
              <div style={{ fontSize: '13px', lineHeight: '1.6' }}>
                {litigationGuide.costs.map((cost, index) => (
                  <div key={index} style={{ marginBottom: '4px' }}>
                    <strong>{cost.name}：</strong>{cost.fee}
                  </div>
                ))}
              </div>
            </div>

            <Title level={4}>📄 利用可能なテンプレート</Title>
            
            {/* 按类别分组显示文档 */}
            {['訴状関連', '答弁書関連', '準備書面関連', '手続書類', '上訴関連'].map(category => {
              const categoryDocs = documents.filter(doc => doc.category === category);
              if (categoryDocs.length === 0) return null;
              
              return (
                <div key={category} style={{ marginBottom: 16 }}>
                  <div style={{ 
                    fontSize: '14px', 
                    fontWeight: '500', 
                    color: '#d4380d',
                    marginBottom: '8px',
                    borderBottom: '1px solid #e8e8e8',
                    paddingBottom: '4px'
                  }}>
                    {category}
                  </div>
                  {categoryDocs.map((doc, index) => (
                    <DocumentItem key={index}>
                      <DocumentInfo>
                        <div style={{ fontWeight: 500, marginBottom: 4 }}>
                          {doc.name}
                        </div>
                        <div style={{ fontSize: '12px', color: '#666' }}>
                          {doc.description}
                        </div>
                      </DocumentInfo>
                      <DocumentActions>
                        <Button 
                          size="small" 
                          icon={<EyeOutlined />}
                          onClick={() => handlePreview((doc as any).id || doc.name, doc.name)}
                        >
                          プレビュー
                        </Button>
                        <Button 
                          size="small" 
                          type="primary"
                          icon={<DownloadOutlined />}
                          onClick={() => handleDownload((doc as any).id || doc.name, doc.name)}
                        >
                          ダウンロード
                        </Button>
                      </DocumentActions>
                    </DocumentItem>
                  ))}
                </div>
              );
            })}

            {/* 程序流程 */}
            <div style={{ marginTop: 20, padding: 16, background: '#f8f9fa', borderRadius: 6 }}>
              <Title level={5}>📋 訴訟手続きの流れ</Title>
              <List size="small">
                {litigationGuide.procedureSteps.map((step, index) => (
                  <List.Item key={index}>{index + 1}. {step}</List.Item>
                ))}
              </List>
            </div>

            {/* 实用链接 */}
            <div style={{ marginTop: 16, padding: 12, background: '#fff7e6', borderRadius: 6 }}>
              <Title level={5}>🔗 参考リンク</Title>
              <div style={{ fontSize: '12px', lineHeight: '1.6' }}>
                {litigationGuide.referenceLinks.map((link, index) => (
                  <div key={index}>
                    • <a href={link.url} target="_blank" rel="noopener noreferrer">{link.name}</a>
                  </div>
                ))}
              </div>
            </div>

            {/* 注意事项 */}
            <div style={{ marginTop: 16, padding: 12, background: '#fff1f0', borderRadius: 6 }}>
              <Title level={5}>⚠️ 重要注意事項</Title>
              <div style={{ fontSize: '12px', lineHeight: '1.6', color: '#cf1322' }}>
                • これらのテンプレートは参考資料であり、法的助言ではありません<br/>
                • 実際の訴訟では、専門家（弁護士）に相談することを強く推奨します<br/>
                • 訴訟は複雑な手続きであり、専門知識が必要です<br/>
                • 提出前に必ず裁判所の窓口で確認してください
              </div>
            </div>
          </ModalContent>
        </Modal>

        {/* テンプレートプレビューモーダル */}
        <TemplatePreviewModal
          visible={previewModal.visible}
          onClose={closePreviewModal}
          templateId={previewModal.templateId}
          templateName={previewModal.templateName}
        />
      </>
    );
  }

  // 如果是弁護士依頼策略，显示特殊内容
  if (selectedStrategy === 'lawyer_legal_proceedings') {
    return (
      <>
        <Modal
          title="⚖️ 弁護士依頼サポート"
          open={visible}
          onCancel={onClose}
          footer={null}
          width={800}
        >
          <ModalContent>
            <div style={{ marginBottom: 20 }}>
              <Text>
                弁護士への依頼に必要な書類と手続きをご案内します。適切な弁護士選定と依頼手続きをサポートします。
              </Text>
            </div>

            {/* 弁護士事務所类型选择指南 */}
            <div style={{ marginBottom: 24, padding: 16, background: '#f0f8ff', borderRadius: 6 }}>
              <Title level={5}>🏛️ 弁護士事務所の選択ガイド</Title>
              <div style={{ fontSize: '13px', lineHeight: '1.6' }}>
                {lawyerGuide.lawyerTypes.map((type, index) => (
                  <div key={index} style={{ marginBottom: '12px' }}>
                    <strong>{type.name}：</strong>{type.description}
                    <div style={{ marginLeft: '16px', fontSize: '12px', color: '#666' }}>
                      <strong>メリット：</strong>{type.advantages.join('、')}
                    </div>
                    <div style={{ marginLeft: '16px', fontSize: '12px', color: '#666' }}>
                      <strong>デメリット：</strong>{type.disadvantages.join('、')}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* 弁護士選定基準 */}
            <div style={{ marginBottom: 24, padding: 16, background: '#fff7e6', borderRadius: 6 }}>
              <Title level={5}>📋 弁護士選定の重要基準</Title>
              <div style={{ fontSize: '13px', lineHeight: '1.6' }}>
                {lawyerGuide.selectionCriteria.map((criteria, index) => (
                  <div key={index} style={{ marginBottom: '8px' }}>
                    <strong>{criteria.name}（重要度：{criteria.importance}）：</strong>{criteria.description}
                  </div>
                ))}
              </div>
            </div>

            {/* 費用体系 */}
            <div style={{ marginBottom: 24, padding: 16, background: '#f6ffed', borderRadius: 6 }}>
              <Title level={5}>💰 弁護士費用の体系</Title>
              <div style={{ fontSize: '13px', lineHeight: '1.6' }}>
                {lawyerGuide.feeStructure.map((fee, index) => (
                  <div key={index} style={{ marginBottom: '8px' }}>
                    <strong>{fee.name}：</strong>{fee.description}
                    <div style={{ marginLeft: '16px', fontSize: '12px', color: '#666' }}>
                      相場：{fee.typicalRange}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* 申请所需文件 */}
            <div style={{ marginBottom: 24 }}>
              <Title level={5}>📝 弁護士依頼に必要な書類</Title>
              <List size="small">
                {lawyerGuide.requiredDocuments.map((doc, index) => (
                  <List.Item key={index}>• {doc}</List.Item>
                ))}
              </List>
            </div>

            <Title level={4}>📄 利用可能なテンプレート</Title>
            
            {/* 按类别分组显示文档 */}
            {['委任関連', '相談・依頼関連', '選定・準備関連'].map(category => {
              const categoryDocs = documents.filter(doc => doc.category === category);
              if (categoryDocs.length === 0) return null;
              
              return (
                <div key={category} style={{ marginBottom: 16 }}>
                  <div style={{ 
                    fontSize: '14px', 
                    fontWeight: '500', 
                    color: '#722ed1',
                    marginBottom: '8px',
                    borderBottom: '1px solid #e8e8e8',
                    paddingBottom: '4px'
                  }}>
                    {category}
                  </div>
                  {categoryDocs.map((doc, index) => (
                    <DocumentItem key={index}>
                      <DocumentInfo>
                        <div style={{ fontWeight: 500, marginBottom: 4 }}>
                          {doc.name}
                        </div>
                        <div style={{ fontSize: '12px', color: '#666' }}>
                          {doc.description}
                        </div>
                      </DocumentInfo>
                      <DocumentActions>
                        <Button 
                          size="small" 
                          icon={<EyeOutlined />}
                          onClick={() => handlePreview((doc as any).id || doc.name, doc.name)}
                        >
                          プレビュー
                        </Button>
                        <Button 
                          size="small" 
                          type="primary"
                          icon={<DownloadOutlined />}
                          onClick={() => handleDownload((doc as any).id || doc.name, doc.name)}
                        >
                          ダウンロード
                        </Button>
                      </DocumentActions>
                    </DocumentItem>
                  ))}
                </div>
              );
            })}

            {/* 程序流程 */}
            <div style={{ marginTop: 20, padding: 16, background: '#f8f9fa', borderRadius: 6 }}>
              <Title level={5}>📋 弁護士依頼の流れ</Title>
              <List size="small">
                {lawyerGuide.procedureSteps.map((step, index) => (
                  <List.Item key={index}>{index + 1}. {step}</List.Item>
                ))}
              </List>
            </div>

            {/* 实用链接 */}
            <div style={{ marginTop: 16, padding: 12, background: '#fff7e6', borderRadius: 6 }}>
              <Title level={5}>🔗 参考リンク</Title>
              <div style={{ fontSize: '12px', lineHeight: '1.6' }}>
                {lawyerGuide.referenceLinks.map((link, index) => (
                  <div key={index}>
                    • <a href={link.url} target="_blank" rel="noopener noreferrer">{link.name}</a>
                  </div>
                ))}
              </div>
            </div>

            {/* 注意事项 */}
            <div style={{ marginTop: 16, padding: 12, background: '#fff1f0', borderRadius: 6 }}>
              <Title level={5}>⚠️ 重要注意事項</Title>
              <div style={{ fontSize: '12px', lineHeight: '1.6', color: '#cf1322' }}>
                • これらのテンプレートは参考資料であり、法的助言ではありません<br/>
                • 弁護士選定は慎重に行い、複数の弁護士に相談することをお勧めします<br/>
                • 費用については事前に十分に協議してください<br/>
                • 相性も重要な要素です。信頼できる弁護士を選んでください
              </div>
            </div>
          </ModalContent>
        </Modal>

        {/* テンプレートプレビューモーダル */}
        <TemplatePreviewModal
          visible={previewModal.visible}
          onClose={closePreviewModal}
          templateId={previewModal.templateId}
          templateName={previewModal.templateName}
        />
      </>
    );
  }

  return (
    <>
      <Modal
        title="📋 書類作成サポート"
        open={visible}
        onCancel={onClose}
        footer={null}
        width={600}
      >
        <ModalContent>
          <div style={{ marginBottom: 20 }}>
            <Text>
              選択された戦略「{getStrategyName(selectedStrategy || '')}」で必要な書類について詳しくご案内できます。
            </Text>
          </div>

          <Title level={4}>📄 利用可能なテンプレート</Title>
          
          {documents.map((doc, index) => (
            <DocumentItem key={index}>
              <DocumentInfo>
                <div style={{ fontWeight: 500, marginBottom: 4 }}>
                  {doc.name}
                </div>
                <div style={{ fontSize: '12px', color: '#666' }}>
                  {doc.description}
                </div>
              </DocumentInfo>
              <DocumentActions>
                <Button 
                  size="small" 
                  icon={<EyeOutlined />}
                  onClick={() => handlePreview(doc.name, doc.name)}
                >
                  プレビュー
                </Button>
                <Button 
                  size="small" 
                  type="primary"
                  icon={<DownloadOutlined />}
                  onClick={() => handleDownload(doc.name, doc.name)}
                >
                  ダウンロード
                </Button>
              </DocumentActions>
            </DocumentItem>
          ))}

          <div style={{ marginTop: 20, padding: 16, background: '#f0f8ff', borderRadius: 6 }}>
            <Title level={5}>📋 作成手順</Title>
            <List size="small">
              <List.Item>1. テンプレートをダウンロード</List.Item>
              <List.Item>2. 必要事項を記入</List.Item>
              <List.Item>3. 関連書類を添付</List.Item>
              <List.Item>4. 提出先に確認</List.Item>
            </List>
          </div>
        </ModalContent>
      </Modal>

      {/* テンプレートプレビューモーダル */}
      <TemplatePreviewModal
        visible={previewModal.visible}
        onClose={closePreviewModal}
        templateId={previewModal.templateId}
        templateName={previewModal.templateName}
      />
    </>
  );
}; 